import { ChangeDetectionStrategy, Component, ElementRef, computed, input, output, signal, viewChild } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { CollaboratorDto } from '../../../services/api.clients.generated';
import { getContactAvatarColor, getContactInitials, getContactName } from '../../contact-display.utils';

/**
 * Pill dropdown to assign an ECAB collaborator to a message. Opens a searchable
 * popup listing collaborators plus a "Non assigné" option. Presentational.
 */
@Component({
  selector: 'app-assignee-picker',
  imports: [OverlayModule, MatIcon, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './assignee-picker.component.html',
  styleUrl: './assignee-picker.component.scss'
})
export class AssigneePickerComponent {
  readonly collaborators = input<CollaboratorDto[]>([]);
  readonly assignedId = input<string | undefined>(undefined);
  readonly assigneeChange = output<CollaboratorDto | null>();

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly open = signal(false);
  protected readonly search = signal('');

  protected readonly selected = computed(() => {
    const id = this.assignedId();
    return id ? this.collaborators().find(collaborator => collaborator.id === id) ?? null : null;
  });

  protected readonly filtered = computed(() => {
    const term = this.search().trim().toLowerCase();
    const collaborators = this.collaborators();
    if (!term) {
      return collaborators;
    }
    return collaborators.filter(collaborator =>
      `${collaborator.firstname} ${collaborator.lastname}`.toLowerCase().includes(term)
      || (collaborator.email?.toLowerCase().includes(term) ?? false));
  });

  protected initials(collaborator: CollaboratorDto): string {
    return getContactInitials(collaborator);
  }

  protected color(collaborator: CollaboratorDto): string {
    return getContactAvatarColor(collaborator);
  }

  protected name(collaborator: CollaboratorDto): string {
    return getContactName(collaborator, collaborator.email ?? '');
  }

  protected toggle(): void {
    this.open.update(value => !value);
  }

  protected close(): void {
    this.open.set(false);
    this.search.set('');
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected onPanelAttached(): void {
    setTimeout(() => this.searchInput()?.nativeElement.focus());
  }

  protected select(collaborator: CollaboratorDto | null): void {
    const currentId = this.assignedId();
    this.close();
    if ((collaborator?.id ?? null) !== (currentId ?? null)) {
      this.assigneeChange.emit(collaborator);
    }
  }
}
